import React, {useState, useEffect, useRef} from 'react';
import {useParams, useNavigate, useSearchParams} from 'react-router';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Paper,
    Button,
} from '@mui/material';
import {
    Home as HomeIcon,
    School as SchoolIcon,
    Topic as TopicIcon,
    ArrowBack as BackIcon,
} from '@mui/icons-material';

// Components
import QuestionRenderer from './components/QuestionRenderer.jsx';
import QuestionNavigation from './components/QuestionNavigation.jsx';
import TopicProgress from './components/TopicProgress.jsx';
import SubmissionStatus from './components/SubmissionStatus.jsx';
import SubmissionSummary from './components/SubmissionSummary.jsx';
import SubmissionHistory from './components/SubmissionHistory.jsx';
import {GamificationRewardsModal} from '../../components/GamificationRewardsModal';
import {FloatingXpReward} from '../../components/FloatingXpReward';
import {GamificationConfetti} from '../../components/GamificationConfetti';

// Hooks
import {useTopicData} from './hooks/useTopicData.js';
import {useSubmission} from './hooks/useSubmission.js';
import {useSubmissionHistory} from './hooks/useSubmissionHistory.js';
import {GamificationService} from '../../services/GamificationService';

const StudentTopicPage = ({accessToken, claims}) => {
    // Routing and URL parameters
    const {courseId, topicId} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // State management
    // Quiz navigation state
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [completedSections, setCompletedSections] = useState(0);

    // View management
    const [currentView, setCurrentView] = useState('quiz');
    const [detailedSubmissionData, setDetailedSubmissionData] = useState(null);

    // Summary submission data state
    const [summarySubmissionData, setSummarySubmissionData] = useState(null);
    const [loadingSummaryData, setLoadingSummaryData] = useState(false);

    // Gamification state
    const [showGamificationRewards, setShowGamificationRewards] = useState(false);
    const [showFloatingXp, setShowFloatingXp] = useState(false);
    const [floatingXpData, setFloatingXpData] = useState({xp: 0, coins: 0});

    // Refs for preventing duplicate API calls
    const hasTriedToFetchHistory = useRef(false);

    // Hooks for data fetching
    const {
        course,
        topic,
        formData,
        allQuestions,
        loading: topicLoading,
        error: topicError
    } = useTopicData(accessToken, courseId, topicId);

    const {
        submissionId,
        answers,
        feedback,
        isSubmitted,
        submitSuccess,
        submitting,
        error: submissionError,
        gamificationRewards,
        startSubmission,
        submitSection,
        completeSubmission,
        restartSubmission,
        updateAnswer,
        setError
    } = useSubmission(accessToken, formData?.id);

    const {
        history,
        loading: historyLoading,
        error: historyError,
        fetchHistory,
        fetchDetailedSubmission
    } = useSubmissionHistory(accessToken, formData?.id);

    // Derived values and computed state for rendering
    const derivedAllQuestions = allQuestions?.length > 0 ? allQuestions : (formData?.questions || []);
    const loading = topicLoading;
    const error = topicError || submissionError || historyError;
    const showConfetti = submitSuccess || (gamificationRewards && showGamificationRewards);

    // Current question and section info
    const currentQuestion = derivedAllQuestions[currentQuestionIndex] || null;
    const currentSection = formData?.sections?.[currentSectionIndex] || null;
    const totalSections = formData?.sections?.length || 0;
    const currentSectionQuestions = derivedAllQuestions.filter(q => q.sectionId === currentSection?.id);
    const currentQuestionIndexInSection = currentSectionQuestions.findIndex(q => q.id === currentQuestion?.id);

    // Use effect hooks for managing side effects and data fetching
    // Auto-start submission when form data becomes available
    useEffect(() => {
        if (formData && !submissionId && !isSubmitted && currentView === 'quiz') {
            startSubmission();
        }
    }, [formData, submissionId, isSubmitted, currentView]);

    // Show gamification rewards when available
    useEffect(() => {
        if (gamificationRewards && submitSuccess) {
            setShowGamificationRewards(true);
        }
    }, [gamificationRewards, submitSuccess]);

    // Safety mechanism: Prevent falling back to quiz mode for completed submissions
    useEffect(() => {
        if (submitSuccess && currentView === 'quiz') {
            setCurrentView('summary');
        }
    }, [submitSuccess, currentView]);

    // Safety mechanism: Redirect completed submissions from detailed view
    useEffect(() => {
        if (detailedSubmissionData && currentView === 'quiz') {
            setCurrentView('detailed');
        }
    }, [detailedSubmissionData, currentView]);

    // Fetch summary submission data when in summary view
    useEffect(() => {
        const fetchSummaryData = async () => {
            if (submissionId &&
                currentView === 'summary' &&
                !summarySubmissionData &&
                !loadingSummaryData &&
                !detailedSubmissionData) { // Don't fetch if we already have detailed data

                setLoadingSummaryData(true);
                try {
                    const detailedData = await fetchDetailedSubmission(submissionId);

                    // Enhance with question data
                    const allQuestions = formData?.sections?.flatMap(section =>
                        section.questions?.map(q => ({...q, sectionId: section.id})) || []
                    ) || [];

                    const enhancedAnswers = detailedData.answers.map(answer => {
                        const question = allQuestions.find(q => q.id === answer.question_id);
                        return {
                            ...answer,
                            question: question || {
                                id: answer.question_id,
                                text: 'Question not found',
                                type: 'text',
                                config: {}
                            }
                        };
                    });

                    setSummarySubmissionData({
                        ...detailedData,
                        answers: enhancedAnswers
                    });
                } catch (err) {
                    console.error('Failed to fetch submission details for summary:', err);
                    // Fall back to the current approach if API call fails
                } finally {
                    setLoadingSummaryData(false);
                }
            }
        };

        fetchSummaryData();
    }, [submissionId, currentView, summarySubmissionData, loadingSummaryData, detailedSubmissionData, formData, fetchDetailedSubmission]);

    // Reset summary data when view changes away from summary
    useEffect(() => {
        if (currentView !== 'summary') {
            setSummarySubmissionData(null);
            setLoadingSummaryData(false);
        }
    }, [currentView]);

    // Handle URL navigation and history fetching
    useEffect(() => {
        const viewParam = searchParams.get('view');
        const sourceParam = searchParams.get('source');

        console.log('=== URL CHANGE EFFECT ===');
        console.log('View param:', viewParam);
        console.log('Source param:', sourceParam);
        console.log('Current view:', currentView);
        console.log('Submit success:', submitSuccess);

        // Reset the flag when view changes away from history
        if (currentView !== 'history') {
            hasTriedToFetchHistory.current = false;
        }

        // Handle URL parameter changes
        if (viewParam === 'history' && currentView !== 'history') {
            console.log('Setting view to history');
            setCurrentView('history');
        } else if (viewParam === 'detailed' && currentView !== 'detailed') {
            console.log('Setting view to detailed');
            setCurrentView('detailed');
        } else if (!viewParam && currentView === 'history') {
            // Coming back from history without view param
            if (submitSuccess) {
                console.log('Setting view to summary (back from history with submitSuccess)');
                setCurrentView('summary');
            } else {
                console.log('Setting view to quiz (back from history without submitSuccess)');
                setCurrentView('quiz');
            }
        } else if (!viewParam && submitSuccess && currentView !== 'summary' && currentView !== 'history') {
            // No view param but we have submitSuccess - should be summary
            console.log('Setting view to summary (no param, but submitSuccess)');
            setCurrentView('summary');
        }

        // Fetch history if conditions are met and we haven't tried yet
        if (currentView === 'history' &&
            formData?.id &&
            !topicLoading &&
            !hasTriedToFetchHistory.current &&
            !history &&
            !historyLoading) {

            hasTriedToFetchHistory.current = true;
            fetchHistory();
        }
    }, [searchParams, formData?.id, topicLoading, currentView, history, historyLoading, submitSuccess]);

    // Event handlers for question navigation and submission
    const handleQuestionChange = (newIndex) => {
        if (newIndex >= 0 && newIndex < derivedAllQuestions.length) {
            setCurrentQuestionIndex(newIndex);

            // Update section index if we moved to a different section
            const newQuestion = derivedAllQuestions[newIndex];
            const newSectionIndex = formData?.sections?.findIndex(s => s.id === newQuestion.sectionId) || 0;
            setCurrentSectionIndex(newSectionIndex);
        }
    };

    const handleBackToQuiz = () => {
        setCurrentView('quiz');
        setDetailedSubmissionData(null);
        navigate(`/student/course/${courseId}/topic/${topicId}`, {replace: true});
    };

    const handleBackToCourse = () => {
        navigate(`/student/course/${courseId}`, {replace: true});
    };

    const handleBackToHistory = () => {
        setCurrentView('history');
        setDetailedSubmissionData(null);
        // Update URL back to history view
        navigate(`/student/course/${courseId}/topic/${topicId}?view=history`, {replace: true});
    };

    // Context-aware back handler for history view
    const handleBackFromHistory = () => {
        const source = searchParams.get('source');

        console.log('=== BACK NAVIGATION DEBUG ===');
        console.log('Source parameter:', source);
        console.log('Current view:', currentView);

        if (source === 'result') {
            console.log('-> Going back to SUMMARY page (staying in same page)');
            // Stay on the same page but change view to summary
            setCurrentView('summary');
            // Update URL to remove history parameters but stay on topic page
            navigate(`/student/course/${courseId}/topic/${topicId}`, {replace: true});
        } else {
            console.log('-> Navigation back to COURSE page using navigate');
            // Use navigate instead of window.location.href to prevent zoom issues
            navigate(`/student/course/${courseId}`, {replace: true});
        }
    };

    // event handler for submitting the current section
    const handleSubmitSection = async () => {
        if (submitting) {
            return;
        }

        try {
            await submitSection(currentSectionQuestions);

            // Show floating reward for correct answers
            showFloatingReward(currentSectionQuestions.map(q => feedback[q.id]).filter(Boolean));

            // Mark section as completed
            setCompletedSections(prev => prev + 1);

            // Move to next section or complete
            if (currentSectionIndex < totalSections - 1) {
                const nextSectionFirstQuestion = derivedAllQuestions.findIndex(q =>
                    q.sectionId === formData.sections[currentSectionIndex + 1].id
                );
                if (nextSectionFirstQuestion !== -1) {
                    handleQuestionChange(nextSectionFirstQuestion);
                }
            } else {
                await completeSubmission();
                setCurrentView('summary');
            }
        } catch (err) {
            // Handle error silently or with user-facing notification
        }
    };

    const handleRestart = async () => {
        await restartSubmission();
        setCurrentQuestionIndex(0);
        setCurrentSectionIndex(0);
        setCompletedSections(0);
        setCurrentView('quiz');
    };

    const handleViewHistory = () => {
        console.log('=== VIEW HISTORY CLICKED ===');
        console.log('Current view before:', currentView);
        console.log('Submit success:', submitSuccess);

        // Navigate first, then set view
        navigate(`/student/course/${courseId}/topic/${topicId}?view=history&source=result`, {replace: true});

        // Set view after a small delay to ensure URL change is processed
        setTimeout(() => {
            setCurrentView('history');

            if (formData?.id) {
                fetchHistory();
            }
        }, 50);
    };

    const handleViewDetailedSubmission = async (submissionId) => {
        try {
            const detailedData = await fetchDetailedSubmission(submissionId);

            // Attach question data to each answer since the API only returns question_id
            // Build a flat list of all questions from all sections
            const allQuestions = formData?.sections?.flatMap(section =>
                section.questions?.map(q => ({...q, sectionId: section.id})) || []
            ) || [];

            const enhancedAnswers = detailedData.answers.map(answer => {
                const question = allQuestions.find(q => q.id === answer.question_id);

                return {
                    ...answer,
                    question: question || {
                        id: answer.question_id,
                        text: 'Question not found',
                        type: 'text',
                        config: {}
                    }
                };
            });

            const enhancedDetailedData = {
                ...detailedData,
                answers: enhancedAnswers
            };

            setDetailedSubmissionData(enhancedDetailedData);
            setCurrentView('detailed');
            // Update URL to detailed view
            navigate(`/student/course/${courseId}/topic/${topicId}?view=detailed`, {replace: true});
        } catch (err) {
            setError('Failed to load submission details');
        }
    };


    //Helper function to show floating XP rewards
    const showFloatingReward = (feedback) => {
        const correctAnswers = feedback.filter(f => f.is_correct);
        if (correctAnswers.length > 0) {
            const xp = correctAnswers.length * 10; // 10 XP per correct answer
            const coins = correctAnswers.length * 5; // 5 coins per correct answer

            setFloatingXpData({xp, coins});
            setShowFloatingXp(true);
        }
    };

    const isCurrentSectionComplete = () => {
        return currentSectionQuestions.every(q => {
            const answer = answers[q.id];
            if (!q.required) return true;
            return answer !== undefined && answer !== null && answer !== '';
        });
    };

    const calculateScores = () => {
        let totalScore = 0;
        let maxScore = 0;

        Object.values(feedback).forEach(fb => {
            if (fb.points_earned !== null) {
                totalScore += fb.points_earned;
            }
            maxScore += fb.max_points || 1;
        });

        return {totalScore, maxScore};
    };

    // Rendering functions

    const renderQuizView = () => (
        <>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{mb: 3}}>
                <Link
                    color="inherit"
                    href="/student/dashboard"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <HomeIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    Dashboard
                </Link>
                <Link
                    color="inherit"
                    href={`/student/course/${courseId}`}
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <SchoolIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    {course?.title || 'Course'}
                </Link>
                <Typography
                    color="text.primary"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <TopicIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    {topic?.name || 'Topic'}
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{mb: 4}}>
                <Typography variant="h3" gutterBottom>
                    {topic?.name}
                </Typography>
                {topic?.description && (
                    <Typography variant="body1" color="text.secondary">
                        {topic.description}
                    </Typography>
                )}
            </Box>

            {/* Progress */}
            <Box sx={{mb: 4}}>
                <TopicProgress
                    allQuestions={derivedAllQuestions}
                    answers={answers}
                    currentQuestionIndex={currentQuestionIndex}
                    currentSectionIndex={currentSectionIndex}
                    completedSections={completedSections}
                    totalSections={totalSections}
                    currentSection={currentSection}
                    history={history}
                />
            </Box>

            {/* Submission Status */}
            <Box sx={{mb: 4}}>
                <SubmissionStatus
                    submissionId={submissionId}
                    isSubmitted={isSubmitted}
                    submitSuccess={submitSuccess}
                    error={submissionError}
                    onRestart={handleRestart}
                    onViewHistory={handleViewHistory}
                />
            </Box>

            {/* Question Content */}
            {currentQuestion && (
                <Box sx={{mb: 4}}>
                    <QuestionRenderer
                        key={currentQuestion.id}
                        question={currentQuestion}
                        answer={answers[currentQuestion.id]}
                        onAnswerChange={(questionId, answer) => updateAnswer(questionId, answer)}
                        questionNumber={currentQuestionIndex + 1}
                    />

                    <Box sx={{mt: 4}}>
                        <QuestionNavigation
                            questions={currentSectionQuestions}
                            currentQuestionIndex={currentQuestionIndexInSection}
                            onQuestionChange={(index) => {
                                const questionInSection = currentSectionQuestions[index];
                                const globalIndex = derivedAllQuestions.findIndex(q => q.id === questionInSection.id);
                                if (globalIndex !== -1) {
                                    setCurrentQuestionIndex(globalIndex);
                                }
                            }}
                            onSubmitSection={handleSubmitSection}
                            isLastSection={currentSectionIndex === totalSections - 1}
                            submitting={submitting}
                            answers={answers}
                            canSubmit={isCurrentSectionComplete()}
                        />
                    </Box>
                </Box>
            )}
        </>
    );

    const renderCurrentView = () => {
        switch (currentView) {
            case 'summary': {
                const {totalScore, maxScore} = calculateScores();

                // Use API data if available
                if (summarySubmissionData) {
                    return (
                        <SubmissionSummary
                            submission={summarySubmissionData}
                            answers={summarySubmissionData.answers.reduce((acc, answer) => ({
                                ...acc,
                                [answer.question_id]: answer
                            }), {})}
                            formData={formData}
                            onRestart={handleRestart}
                            onViewHistory={handleViewHistory}
                            score={summarySubmissionData.score}
                            maxScore={summarySubmissionData.max_score}
                            accessToken={accessToken}
                            isGamificationModalOpen={showGamificationRewards}
                        />
                    );
                }

                // Show loading while fetching API data
                if (loadingSummaryData) {
                    return (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', my: 4}}>
                            <CircularProgress sx={{mr: 2}}/>
                            <Typography>Loading submission details...</Typography>
                        </Box>
                    );
                }

                // Fallback to current approach if API data not available
                const completeAnswers = {};
                Object.keys(feedback).forEach(questionId => {
                    const userAnswer = answers[questionId];
                    const feedbackData = feedback[questionId];
                    const question = derivedAllQuestions.find(q => q.id === questionId);

                    completeAnswers[questionId] = {
                        id: feedbackData.answer_id || `temp_${questionId}_${submissionId}`, // Add ID for chat functionality
                        question_id: questionId,
                        value: userAnswer,
                        value_text: typeof userAnswer === 'string' ? userAnswer : null,
                        value_json: typeof userAnswer === 'object' ? userAnswer : null,
                        is_correct: feedbackData.is_correct,
                        points_earned: feedbackData.points_earned,
                        max_points: feedbackData.max_points,
                        reasoning: feedbackData.reasoning,
                        question: question
                    };
                });

                return (
                    <SubmissionSummary
                        submission={{id: submissionId}}
                        answers={completeAnswers}
                        formData={formData}
                        onRestart={handleRestart}
                        onViewHistory={handleViewHistory}
                        score={totalScore}
                        maxScore={maxScore}
                        accessToken={accessToken}
                        isGamificationModalOpen={showGamificationRewards}
                    />
                );
            }

            case 'history':
                return (
                    <SubmissionHistory
                        history={history}
                        onBack={handleBackFromHistory}
                        onViewSubmission={handleViewDetailedSubmission}
                        loading={historyLoading}
                    />
                );

            case 'detailed': {
                if (!detailedSubmissionData) return null;

                return (
                    <SubmissionSummary
                        submission={detailedSubmissionData}
                        answers={detailedSubmissionData.answers.reduce((acc, answer) => ({
                            ...acc,
                            [answer.question_id]: answer
                        }), {})}
                        formData={formData}
                        onRestart={null}
                        onViewHistory={handleBackToHistory}
                        score={detailedSubmissionData.score}
                        maxScore={detailedSubmissionData.max_score}
                        accessToken={accessToken} // Pass accessToken prop
                        isGamificationModalOpen={false} // No modal in detailed view
                    />
                );
            }

            case 'quiz':
            default:
                return renderQuizView();
        }
    };

    // Error and loading states

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{mt: 4, mb: 8}}>
                <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{mt: 4, mb: 8}}>
                <Alert severity="error" sx={{mb: 4}}>
                    {error}
                </Alert>
                <Button variant="outlined" onClick={() => navigate(`/student/course/${courseId}`)}>
                    <BackIcon sx={{mr: 1}}/>
                    Back to Course
                </Button>
            </Container>
        );
    }

    if (!formData) {
        return (
            <Container maxWidth="lg" sx={{mt: 4, mb: 8}}>
                <Alert severity="warning" sx={{mb: 4}}>
                    Topic not found.
                </Alert>
                <Button variant="outlined" onClick={() => navigate(`/student/course/${courseId}`)}>
                    <BackIcon sx={{mr: 1}}/>
                    Back to Course
                </Button>
            </Container>
        );
    }


    // Main component render
    return (
        <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
            <GamificationConfetti trigger={showConfetti}/>

            {renderCurrentView()}

            {/* Gamification Rewards Modal */}
            {gamificationRewards && (
                <GamificationRewardsModal
                    open={showGamificationRewards}
                    onClose={() => setShowGamificationRewards(false)}
                    rewards={gamificationRewards}
                    newAchievements={gamificationRewards.new_achievements || []}
                    levelUp={gamificationRewards.level_up || false}
                    newLevel={gamificationRewards.new_level}
                />
            )}

            {/* Floating XP Reward */}
            <FloatingXpReward
                xp={floatingXpData.xp}
                coins={floatingXpData.coins}
                visible={showFloatingXp}
                onComplete={() => setShowFloatingXp(false)}
            />
        </Container>
    );
};

export default StudentTopicPage;